import type { ExternalEventType } from '@pushly/models/lib/structs/external-events/external-event-type'

/**
 * The option one registered event type is chosen by.
 *
 * A name alone does not identify a type: one event name can hold several concurrent schema
 * versions, each with its own property map, so a journey binds to the pair. The composed value is
 * what the select is keyed by; the pair itself is what the trigger stores.
 */
export function formatEventTypeLabel(eventType: ExternalEventType): { value: string; label: string } {
    return {
        value: eventTypeKey(eventType.event_name, eventType.schema_version),
        label: `${eventType.event_name} (v${eventType.schema_version})`,
    }
}

export function eventTypeKey(eventName: string | undefined, schemaVersion: number | undefined): string {
    return `${eventName ?? ''}@${schemaVersion ?? ''}`
}

/** The registered type a trigger's stored name and version resolve to, if the registry still holds it. */
export function findRegisteredEventType(
    eventTypes: ExternalEventType[],
    eventName: string | undefined,
    schemaVersion: number | undefined,
): ExternalEventType | undefined {
    if (!eventName) return undefined
    const key = eventTypeKey(eventName, schemaVersion)
    return eventTypes.find((type) => eventTypeKey(type.event_name, type.schema_version) === key)
}

/**
 * What the surface says about the trigger's current event type.
 *
 * An event type a journey depends on must never be silently unavailable: a journey referencing one
 * the registry no longer holds keeps its stored reference and is reported here, because dropping
 * the reference would leave a journey that looks configured and can never be triggered.
 *
 * Nothing is reported before the registry has loaded — an unresolved name is only news once there
 * is a registry to have failed to find it in.
 */
export function describeEventTypeSelection(
    registryLoaded: boolean,
    eventTypes: ExternalEventType[],
    eventName: string | undefined,
    schemaVersion: number | undefined,
): { warning?: string } {
    if (!registryLoaded || !eventName) return {}
    if (findRegisteredEventType(eventTypes, eventName, schemaVersion)) return {}

    const version = schemaVersion === undefined || schemaVersion === null ? '' : ` (v${schemaVersion})`
    return {
        warning:
            `This journey is triggered by "${eventName}"${version}, which is not registered for this domain. ` +
            'It will not be triggered until that event type is registered or a different one is selected.',
    }
}
